import { Checkbox, Input, Select, Switch, theme } from "antd";
import {
  DashboardV1State,
  DeepLinkType,
  SnippetV1State,
} from "pages/api/deep-links/types";
import { useEffect, useRef, useState } from "react";
import { Flex } from "src/components/Flex";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { cloneData } from "src/utils/objects";
import { ParamsPanel } from "../explore/ParamsPanel";
import { ResponseCanvas } from "../explore/ResponseCanvas";
import { ResponsePanels } from "../explore/ResponsePanels";
import { CardEditor } from "./CardEditor";
import { useQuestionCard } from "./QuestionViewer";
import { CloseOutlined } from "@ant-design/icons";
import { Section } from "../entity/[entityId]/entity/components/Section";

export function QuestionEditor({
  card,
  updateCard,
  onClose,
  okText,
}: {
  card: DashboardV1State["data"][string];
  updateCard(data: typeof card): void;
  onClose(): void;
  okText?: string;
}) {
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("Expected question card");

  const snippets = useDeepLinks(DeepLinkType.SNIPPET);
  const snippetTags =
    snippets.links.data?.reduce((a, c) => {
      const json = c.json as SnippetV1State | undefined;
      if (json?.isForCards) return [...a, ...(json?.tags || [])];
      return a;
    }, [] as string[]) || [];
  const { token } = theme.useToken();
  const [working, updateWorking] = useState(cloneData(card));
  const { explore } = useQuestionCard({
    json: { data: { working: card } },
    cardId: "working",
    searchState: {},
  });

  const viz = useRef({
    value: explore.view.value,
    config: explore.view.config,
  });
  viz.current = { value: explore.view.value, config: explore.view.config };
  const tripwire = `${explore.params.serialized}-${JSON.stringify(
    viz.current
  )}`;
  const params = useRef(explore.params.state);
  params.current = explore.params.state;
  useEffect(() => {
    updateWorking((state) => ({
      ...state,
      state: {
        viz: viz.current,
        params: params.current,
      },
    }));
  }, [tripwire]);
  return (
    <CardEditor
      title="Card Editor"
      onCancel={() => onClose()}
      okText={okText}
      onOk={() => {
        updateCard(working);
        onClose();
      }}
    >
      <Flex
        start
        gap={32}
        style={{
          flex: 1,
          height: "100%",
          width: "100%",
        }}
      >
        <div style={{ flex: 1, minWidth: 0, maxWidth: "100%" }}>
          <Flex gap={16} style={{ width: "100%", marginBottom: 24 }}>
            <Input
              placeholder="Question Title"
              value={working.title}
              onChange={(e) =>
                updateWorking({ ...working, title: e.target.value })
              }
            />
          </Flex>
          <div
            style={{
              margin: "0 0 24px 0",
              width: "100%",
              borderRadius: 4,
              backgroundColor: token.colorBgContainer,
            }}
          >
            <ParamsPanel
              context={explore}
              noTypeSwitching
              expandOnFocus={false}
              floatAway={false}
              collapsed={false}
            />
          </div>
          <div
            style={{
              position: "relative",
              borderRadius: 8,
              overflow: "hidden",
              justifyContent: "center",
              height: "100%",
              paddingTop: "2vh",
            }}
          >
            <div
              style={{
                marginBottom: 12,
                fontSize: 14,
                fontWeight: 600,
                color: token.colorTextHeading,
                paddingBottom: 8,
              }}
            >
              Preview
            </div>
            <div
              style={{
                zIndex: 1,
                backgroundColor: token.colorBgContainer,
                height: "40vh",
                overflow: "visible",
                borderRadius: 8,
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  padding: 4,
                  overflow: "hidden",
                  marginTop: "32px",
                  paddingBottom: "32px",
                }}
              >
                <ResponseCanvas context={explore} />
              </div>
            </div>
          </div>
        </div>
        <div className="chart-right-panel">
          <div
            style={{
              flex: 1,
            }}
          >
            <div
              style={{
                paddingBottom: "16px",
              }}
            >
              <div className="section-header" style={{ marginTop: 0 }}>
                Card Options
              </div>
              <Section>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {/* Card Options Section */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <Checkbox
                      checked={working.showTitle}
                      onChange={(e) =>
                        updateWorking({
                          ...working,
                          showTitle: e.target.checked,
                        })
                      }
                      style={{ marginRight: 0 }}
                    >
                      Display Title
                    </Checkbox>

                    {working.showTitle && (
                      <Checkbox
                        checked={working.centerTitle}
                        onChange={(e) =>
                          updateWorking({
                            ...working,
                            centerTitle: e.target.checked,
                          })
                        }
                        style={{ marginRight: 0 }}
                      >
                        Center
                      </Checkbox>
                    )}
                  </div>

                  {/* Background Toggle */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: token.colorText,
                      }}
                    >
                      Transparent Background
                    </span>
                    <Switch
                      checked={working.transparent}
                      onChange={(checked) =>
                        updateWorking({ ...working, transparent: checked })
                      }
                      size="small"
                    />
                  </div>
                </div>
              </Section>
            </div>
            <div className="section">
              <div style={{ borderRadius: 8 }}>
                <ResponsePanels collapsed={false} context={explore} />
              </div>
            </div>

            <div className="section">
              <div className="section-header">Snippet Tags</div>
              <div style={{ width: "100%" }}>
                <Select
                  value={working.snippetTags || []}
                  mode="tags"
                  showSearch
                  allowClear
                  maxTagCount="responsive"
                  style={{
                    width: "100%",
                    maxWidth: "100%",
                  }}
                  options={[...new Set(snippetTags)].map((tag) => ({
                    value: tag,
                    label: (
                      <div
                        className="hover-parent"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          width: "100%",
                          position: "relative",
                        }}
                      >
                        <span>{tag}</span>
                        {working.snippetTags?.includes(tag) && (
                          <CloseOutlined
                            className="hover-show"
                            style={{
                              fontSize: 10,
                              color: token.colorText,
                              cursor: "pointer",
                              padding: 2,
                              position: "absolute",
                              right: 4,
                              opacity: 0,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              updateWorking({
                                ...working,
                                snippetTags: (working.snippetTags || []).filter(
                                  (t) => t !== tag
                                ),
                              });
                            }}
                          />
                        )}
                      </div>
                    ),
                  }))}
                  tagRender={({ label, value, closable, onClose }) => (
                    <div
                      className="hover-parent"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "2px 7px",
                        borderRadius: "4px",
                        backgroundColor: token.colorFillSecondary,
                        marginRight: 4,
                        fontSize: 13,
                        gap: 4,
                        position: "relative",
                        paddingRight: 24,
                      }}
                    >
                      {typeof label === "string" ? label : value}
                      <CloseOutlined
                        className="hover-show"
                        style={{
                          fontSize: 10,
                          color: "#000000",
                          cursor: "pointer",
                          padding: 2,
                          position: "absolute",
                          right: 4,
                          opacity: 0,
                          transition: "opacity 0.2s",
                        }}
                        onClick={onClose}
                      />
                    </div>
                  )}
                  onChange={(values) =>
                    updateWorking({
                      ...working,
                      snippetTags: values.filter(Boolean),
                    })
                  }
                  onDeselect={(value) => {
                    const currentTags = working.snippetTags || [];
                    updateWorking({
                      ...working,
                      snippetTags: currentTags.filter((tag) => tag !== value),
                    });
                  }}
                  placeholder="Add or select snippet tags"
                  listHeight={300}
                  menuItemSelectedIcon={null}
                  dropdownStyle={{
                    minWidth: 250,
                    maxWidth: "100%",
                  }}
                  removeIcon={<CloseOutlined style={{ fontSize: 12 }} />}
                  dropdownRender={(menu) => {
                    const selectedTags = working.snippetTags || [];
                    return (
                      <div>
                        {menu}
                        {selectedTags.length > 0 && (
                          <div
                            style={{
                              padding: "8px 12px",
                              borderTop: `1px solid ${token.colorBorder}`,
                              color: token.colorTextSecondary,
                              fontSize: 13,
                              lineHeight: "1.4",
                              backgroundColor: token.colorBgElevated,
                            }}
                          >
                            <div
                              style={{
                                marginBottom: 4,
                                color: token.colorTextDescription,
                              }}
                            >
                              Selected Tags ({selectedTags.length})
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 4,
                              }}
                            >
                              {selectedTags.map((tag) => (
                                <div
                                  key={tag}
                                  className="hover-parent"
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "2px 7px",
                                    borderRadius: "4px",
                                    backgroundColor: token.colorFillSecondary,
                                    fontSize: 13,
                                    gap: 4,
                                    cursor: "default",
                                    position: "relative",
                                    paddingRight: 24,
                                  }}
                                >
                                  {tag}
                                  <CloseOutlined
                                    className="hover-show"
                                    style={{
                                      fontSize: 10,
                                      color: token.colorText,
                                      cursor: "pointer",
                                      padding: 2,
                                      position: "absolute",
                                      right: 4,
                                      opacity: 0,
                                      transition: "opacity 0.2s",
                                    }}
                                    onClick={() => {
                                      updateWorking({
                                        ...working,
                                        snippetTags: selectedTags.filter(
                                          (t) => t !== tag
                                        ),
                                      });
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }}
                  tokenSeparators={[","]}
                />
              </div>
            </div>
          </div>
        </div>
      </Flex>
    </CardEditor>
  );
}
